import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Row,
  Col,
  Tag,
  Divider,
  Button,
  Avatar,
  theme,
  Space,
  Flex,
  Form,
  Tooltip,
  Progress,
  Card,
  Descriptions,
  Badge,
  Statistic,
  type UploadFile,
} from "antd";
import {
  CalendarOutlined,
  FilePdfOutlined,
  SafetyCertificateOutlined,
  DownloadOutlined,
  EditOutlined,
  SignatureOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SplitCellsOutlined,
  UserOutlined,
  MailOutlined,
  TeamOutlined,
  BankOutlined,
  PercentageOutlined,
  EyeOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import { URL_FILES } from "../../../constans/apis";
import { downloadFileFetch, getPdfUrlFetch } from "../../../lib/files/api/fetchFile";
import type { Contract } from "../types";
import openNotification from "../../../utils/openNotification";
import { useHybridResponsive } from "../../shared";
import PdfEditor from "../../shared/PdfEditor/PdfEditor";
import ContractFrom from "./ContractFrom";
import dayjs from "dayjs";
import type { UploadContractData } from "../types/uploadContract";
import { useEditContract } from "../hooks/useEditContract";
import { formatPaymentMethods, getPaymentMethods } from "../utils/paymentMethods";
import SkeletonNode from "../../shared/components/SkeletonNode";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Helper: format date
// ---------------------------------------------------------------------------
function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Resolución de campos de la plantilla PDF a partir de los datos del contrato
//
// La plantilla (Ca_template_pdf.template_text) es texto libre editado por el
// usuario, así que el nombre exacto de cada [placeholder] puede variar entre
// plantillas (p. ej. "nombre_proveedor" vs "Fecha de término" vs
// "fecha_vencimiento"). Para no depender de una redacción exacta, cada
// placeholder detectado se normaliza y se compara contra un diccionario de
// alias conocidos; si coincide, se autocompleta con el dato real del
// contrato en vez de dejarlo vacío.
// ---------------------------------------------------------------------------
function normalizeTemplateFieldKey(key: string): string {
  return key
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function formatContractDate(value?: string | Date | dayjs.Dayjs): string {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : "";
}

interface TemplateFieldValues {
  clientName: string;
  providerName: string;
  city: string;
  startDate: string;
  endDate: string;
}

const TEMPLATE_FIELD_RESOLVERS: Record<string, (v: TemplateFieldValues) => string> = {
  nombre_cliente: (v) => v.clientName,
  cliente: (v) => v.clientName,
  nombre_proveedor: (v) => v.providerName,
  nombre_prestador: (v) => v.providerName,
  proveedor: (v) => v.providerName,
  prestador: (v) => v.providerName,
  ciudad_estado: (v) => v.city,
  ciudad: (v) => v.city,
  estado: (v) => v.city,
  fecha_de_inicio: (v) => v.startDate,
  fecha_inicio: (v) => v.startDate,
  fecha_de_termino: (v) => v.endDate,
  fecha_termino: (v) => v.endDate,
  fecha_de_vencimiento: (v) => v.endDate,
  fecha_vencimiento: (v) => v.endDate,
  fecha_fin: (v) => v.endDate,
  fecha_de_fin: (v) => v.endDate,
};

function resolveTemplateFields(template: string | undefined, values: TemplateFieldValues): Record<string, string> {
  if (!template) return {};
  const regex = /\[([^\]]+)\]/g;
  const resolved: Record<string, string> = {};
  let match: RegExpExecArray | null;
  while ((match = regex.exec(template)) !== null) {
    const rawKey = match[1];
    const resolver = TEMPLATE_FIELD_RESOLVERS[normalizeTemplateFieldKey(rawKey)];
    if (resolver) {
      resolved[rawKey] = resolver(values);
    }
  }
  return resolved;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface ContractDetailsProps {
  contract: Contract;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const ContractDetails: React.FC<ContractDetailsProps> = ({ contract }) => {
  const { token } = theme.useToken();
  const [editMode, setEditMode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { isMobile } = useHybridResponsive();

  const statusConfig = {
    vigente: { color: "success" as const, label: "Vigente", dotStatus: "success" as const },
    por_vencer: { color: "warning" as const, label: "Por Vencer", dotStatus: "warning" as const },
    finalizado: { color: "default" as const, label: "Finalizado", dotStatus: "default" as const },
  };
  const cfg = statusConfig[contract.status as keyof typeof statusConfig] ?? statusConfig.vigente;

  const paymentMethods = useMemo(() => getPaymentMethods(contract), [contract]);

  // Duration progress
  const durationProgress = useMemo(() => {
    if (!contract.start_date || !contract.end_date) return null;
    const start = new Date(contract.start_date).getTime();
    const end = new Date(contract.end_date).getTime();
    const now = Date.now();
    return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
  }, [contract.start_date, contract.end_date]);

  useEffect(() => {
    return () => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  const handleDownload = async () => {
    if (!contract.uuid_file) {
      openNotification("warning", "Atención", "El contrato no tiene un documento asociado.");
      return;
    }
    setDownloading(true);
    try {
      // @ts-ignore
      await downloadFileFetch(`${URL_FILES}/${contract.uuid_file}`, typeof TOKEN_FILES !== "undefined" ? TOKEN_FILES : undefined);
    } catch {
      openNotification("error", "Error", "Error al descargar el documento.");
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    if (!contract.uuid_file) {
      openNotification("warning", "Atención", "El contrato no tiene un documento asociado.");
      return;
    }
    if (pdfUrl) { setPdfUrl(null); return; }
    setViewing(true);
    try {
      // @ts-ignore
      const url = await getPdfUrlFetch(`${URL_FILES}/${contract.uuid_file}`, typeof TOKEN_FILES !== "undefined" ? TOKEN_FILES : undefined);
      setPdfUrl(url);
    } catch {
      openNotification("error", "Error", "Error al cargar el documento.");
    } finally {
      setViewing(false);
    }
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    fontWeight: 700,
  };

  return (
    <Row gutter={[20, 20]}>
      {/* ═══════════════════════ LEFT / MAIN PANEL ═══════════════════════ */}
      <Col span={24} md={editMode ? 9 : 24}>

        {/* ── Hero card ─────────────────────────────────────────────────── */}
        <Card
          style={{
            marginBottom: 16,
            background: `linear-gradient(135deg, ${token.colorBgContainer} 0%, ${token.colorPrimaryBg} 100%)`,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
          styles={{ body: { padding: 24 } }}
        >
          <Flex gap={16} align="flex-start">
            <Avatar
              size={64}
              icon={contract.icon ?? <SafetyCertificateOutlined />}
              shape="square"
              style={{
                borderRadius: token.borderRadiusLG,
                background: token.colorPrimaryBg,
                color: token.colorPrimary,
                fontSize: 26,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Space size={6} style={{ marginBottom: 6 }}>
                <Tag
                  color={cfg.color}
                  style={{ borderRadius: 999, fontWeight: 700, margin: 0 }}
                >
                  <Badge status={cfg.dotStatus} /> {cfg.label}
                </Tag>
                {contract.id_contract && (
                  <Tag style={{ borderRadius: 999, background: token.colorFillAlter, border: "none" }}>
                    #{contract.id_contract}
                  </Tag>
                )}
              </Space>
              <Title level={4} style={{ margin: 0, fontWeight: 800 }}>
                {contract.contract_name ?? contract.title}
              </Title>
              {contract.id_type_contract && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Tipo #{contract.id_type_contract} · Plantilla #{contract.id_template}
                </Text>
              )}
            </div>
          </Flex>
        </Card>

        {/* ── Client + Provider ─────────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={editMode ? 24 : 12}>
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Cliente</Text>}
              style={{ height: "100%", border: `1px solid ${token.colorBorderSecondary}` }}
            >
              <Flex gap={12} align="center">
                <Avatar
                  icon={<UserOutlined />}
                  style={{ background: token.colorPrimaryBg, color: token.colorPrimary, flexShrink: 0 }}
                />
                <div>
                  <Text strong style={{ display: "block" }}>
                    {contract.client_name} {contract.client_lastname_1} {contract.client_lastname_2}
                  </Text>
                  {contract.client_email && (
                    <Flex align="center" gap={4}>
                      <MailOutlined style={{ fontSize: 11, color: token.colorTextTertiary }} />
                      <Text type="secondary" style={{ fontSize: 12 }}>{contract.client_email}</Text>
                    </Flex>
                  )}
                </div>
              </Flex>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={editMode ? 24 : 12}>
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Proveedor</Text>}
              style={{ height: "100%", border: `1px solid ${token.colorBorderSecondary}` }}
            >
              <Flex gap={12} align="center">
                <Avatar
                  icon={<TeamOutlined />}
                  style={{ background: token.colorSuccessBg, color: token.colorSuccess, flexShrink: 0 }}
                />
                <div>
                  <Text strong style={{ display: "block" }}>
                    {contract.party?.name_company ?? contract.party ?? "—"}
                  </Text>
                  {contract.id_company && (
                    <Text type="secondary" style={{ fontSize: 12 }}>Empresa #{contract.id_company}</Text>
                  )}
                </div>
              </Flex>
            </Card>
          </Col>
        </Row>

        {/* ── Dates ─────────────────────────────────────────────────────── */}
        <Card
          size="small"
          title={<Text type="secondary" style={sectionLabelStyle}>Plazos y Vigencia</Text>}
          style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
        >
          <Descriptions column={{ xs: 1, sm: 3 }} layout={editMode ? "vertical" : "horizontal"} size="small">
            <Descriptions.Item
              label={
                <Flex align="center" gap={4}>
                  <SignatureOutlined />
                  <span>Firma</span>
                </Flex>
              }
            >
              <Text strong>{formatDate(contract.signing_date)}</Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Flex align="center" gap={4}>
                  <CalendarOutlined />
                  <span>Inicio</span>
                </Flex>
              }
            >
              <Text strong>{formatDate(contract.start_date)}</Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Flex align="center" gap={4}>
                  <ClockCircleOutlined />
                  <span>Vencimiento</span>
                </Flex>
              }
            >
              <Text
                strong
                style={{ color: contract.status === "por_vencer" ? token.colorError : token.colorText }}
              >
                {formatDate(contract.end_date)}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {contract.expiresLabel && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Flex align="center" justify="space-between">
                <Text type="secondary" style={{ fontSize: 13 }}>Estado de tiempo</Text>
                <Tag
                  color={contract.status === "por_vencer" ? "error" : "success"}
                  style={{ borderRadius: 999 }}
                >
                  {contract.expiresLabel}
                </Tag>
              </Flex>
            </>
          )}

          {durationProgress !== null && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Flex justify="space-between" style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Progreso del contrato</Text>
                <Text style={{ fontSize: 12 }}>{durationProgress}%</Text>
              </Flex>
              <Progress
                percent={durationProgress}
                showInfo={false}
                size="small"
                strokeColor={
                  durationProgress > 80 ? token.colorError :
                    durationProgress > 50 ? token.colorWarning :
                      token.colorPrimary
                }
              />
            </>
          )}
        </Card>

        {/* ── Payment + Parciales ───────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={contract.partials === "Si" ? 12 : 24} lg={editMode ? 24 : (contract.partials === "Si" ? 12 : 24)}>
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Métodos de Pago</Text>}
              style={{ height: "100%", border: `1px solid ${token.colorBorderSecondary}` }}
            >
              <Flex align="center" gap={10}>
                <Avatar
                  size="small"
                  icon={<BankOutlined />}
                  style={{ background: token.colorInfoBg, color: token.colorInfo }}
                />
                {paymentMethods.length === 0 ? (
                  <Text strong>—</Text>
                ) : (
                  <Flex gap={4} wrap justify="flex-end" style={{ flex: 1 }}>
                    {paymentMethods.map((method) => (
                      <Tag key={method} color="blue" style={{ borderRadius: 999, margin: 0 }}>
                        {method}
                      </Tag>
                    ))}
                  </Flex>
                )}
              </Flex>
            </Card>
          </Col>

          {contract.partials === "Si" && (
            <Col xs={24} sm={12} lg={editMode ? 24 : 12}>
              <Card
                size="small"
                title={<Text type="secondary" style={sectionLabelStyle}>Pagos Parciales</Text>}
                style={{ height: "100%", border: `1px solid ${token.colorBorderSecondary}` }}
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item
                    label={
                      <Flex align="center" gap={4}>
                        <CheckCircleOutlined />
                        <span>Aplica</span>
                      </Flex>
                    }
                  >
                    <Tag color="cyan" style={{ borderRadius: 999 }}>Sí</Tag>
                  </Descriptions.Item>
                  {contract.partials_total !== undefined && (
                    <Descriptions.Item
                      label={
                        <Flex align="center" gap={4}>
                          <SplitCellsOutlined />
                          <span>Total de pagos</span>
                        </Flex>
                      }
                    >
                      <Text strong>{contract.partials_total}</Text>
                    </Descriptions.Item>
                  )}
                  {contract.partials_percentage !== undefined && (
                    <Descriptions.Item
                      label={
                        <Flex align="center" gap={4}>
                          <PercentageOutlined />
                          <span>por pago</span>
                        </Flex>
                      }
                    >
                      <Text strong>{contract.partials_percentage}%</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {/* Installment visual pills */}
                {contract.partials_total && (
                  <Flex gap={4} style={{ marginTop: 10 }}>
                    {Array.from({ length: contract.partials_total }).map((_, i) => (
                      <Tooltip key={i} title={`Pago ${i + 1}: ${contract.partials_percentage}%`}>
                        <div
                          style={{
                            flex: 1,
                            height: 6,
                            borderRadius: 99,
                            //background: i === 0 ? token.colorPrimary : token.colorPrimaryBg,
                            background: token.colorPrimaryBg,
                            cursor: "default",
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Flex>
                )}
              </Card>
            </Col>
          )}
        </Row>

        {/* ── System references ─────────────────────────────────────────── */}
        <Card
          size="small"
          title={<Text type="secondary" style={sectionLabelStyle}>Referencias del Sistema</Text>}
          style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
        >
          <Row gutter={[8, 8]}>
            {[
              { label: "Contrato", value: contract.id_contract },
              { label: "Cliente", value: contract.id_client },
              { label: "Cotización", value: contract.id_quote },
              { label: "Expediente", value: contract.id_record },
            ].map(({ label, value }) => (
              <Col xs={12} sm={6} key={label}>
                <Card
                  size="small"
                  styles={{ body: { textAlign: "center", padding: "12px 8px" } }}
                  style={{ background: token.colorFillAlter, border: "none" }}
                >
                  <Statistic
                    title={<Text style={{ fontSize: 11 }} type="secondary">{label}</Text>}
                    value={value ?? "—"}
                    valueStyle={{ color: token.colorPrimary, fontSize: 20, fontWeight: 700 }}
                    prefix={<NumberOutlined style={{ fontSize: 12 }} />}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        <Divider style={{ margin: "8px 0 16px" }} />

        {/* ── Document card ─────────────────────────────────────────────── */}
        <Card
          size="small"
          title={<Text type="secondary" style={sectionLabelStyle}>Documento Adjunto</Text>}
          extra={
            <Space size={8}>
              {!isMobile && (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  type={editMode ? "primary" : "default"}
                  onClick={() => setEditMode(!editMode)}
                >
                  {editMode ? "Cerrar editor" : "Editar"}
                </Button>
              )}
              <Button size="small" icon={<EyeOutlined />} onClick={handleView} loading={viewing}>
                {pdfUrl ? "Ocultar" : "Ver"}
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={downloading}
              >
                Descargar
              </Button>
            </Space>
          }
          style={{ border: `1px dashed ${token.colorBorder}`, background: token.colorBgLayout }}
        >
          <Flex align="center" gap={14}>
            <Avatar
              size={44}
              icon={<FilePdfOutlined />}
              shape="square"
              style={{
                background: token.colorErrorBg,
                color: token.colorError,
                borderRadius: token.borderRadius,
                flexShrink: 0,
              }}
            />
            <div>
              <Text strong style={{ display: "block" }}>Documento del Contrato</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>Formato PDF Original protegido</Text>
            </div>
          </Flex>

          {pdfUrl && (
            <div
              style={{
                marginTop: 16,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
                overflow: "hidden",
              }}
            >
              <iframe
                src={pdfUrl}
                title="Visor PDF"
                width="100%"
                height="600"
                style={{ border: "none", display: "block" }}
              />
            </div>
          )}
        </Card>
      </Col>

      {/* ═══════════════════════ RIGHT PANEL — editor ════════════════════ */}
      {editMode && (
        <Col span={24} md={15}>
          <ComponentEditor contract={contract} />
        </Col>
      )}
    </Row>
  );
};

export default ContractDetails;

// ---------------------------------------------------------------------------
// ComponentEditor
// ---------------------------------------------------------------------------
const ComponentEditor: React.FC<{ contract: Contract }> = ({ contract }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const formValues = Form.useWatch([], form);
  const debouncedFormValues = useDebounce(formValues, 600);
  const { mutate: editContractMutation, isPending: isEditing } = useEditContract();

  const navigate = useNavigate();


  const handleFinish = async (values: UploadContractData) => {
    try {

      editContractMutation({ ...values, id_contract: contract.id_contract, id_record: contract.id_record }, {
        onSuccess: () => {
          openNotification('success', 'Éxito', 'Contrato guardado correctamente');
        },
        onError: (error) => {
          openNotification('error', 'Error', error.message);
        }
      });
    } catch (error: any) {
      openNotification('error', 'Error', error.message || 'Ocurrió un error inesperado');
    }
  };
  useEffect(() => {
    if (contract) {
      form.setFieldsValue({
        ...contract,
        contract_name: contract.contract_name ?? contract.title,
        payment_methods: getPaymentMethods(contract),
        signing_date: contract.signing_date && dayjs(contract.signing_date),
        start_date: contract.start_date && dayjs(contract.start_date),
        end_date: contract.end_date && dayjs(contract.end_date),
      });
    }
  }, [contract, form]);

  // Las plantillas sustituyen variables por texto, así que los métodos de pago
  // se envían al PDF como una lista separada por comas.
  const currentData = useMemo(() => {
    const merged = { ...contract, ...debouncedFormValues };
    const methods = formatPaymentMethods(merged);

    const resolvedTemplateFields = resolveTemplateFields(merged.template_text, {
      clientName: [merged.client_name, merged.client_lastname_1, merged.client_lastname_2]
        .filter(Boolean)
        .join(" ")
        .trim(),
      providerName: contract.party?.name_company || contract.party?.public_name_company || "",
      city: merged.city || "Colima",
      startDate: formatContractDate(merged.start_date),
      endDate: formatContractDate(merged.end_date),
    });

    return {
      ...merged,
      payment_method_type: methods,
      payment_methods: methods,
      ...resolvedTemplateFields,
    };
  }, [contract, debouncedFormValues]);

  return (
    <>
      {isEditing ? <SkeletonNode rows={10} width="50%" /> : (

        <PdfEditor
          data={currentData}
          template={currentData.template_text}
          documentTitle={currentData.contract_name ?? contract.title ?? ""}
          onTemplateChange={(text) => form.setFieldValue("template_text", text)}
          fieldsForm={
            <ContractFrom
              form={form}
              onFinish={handleFinish}
              fileList={fileList}
              setFileList={setFileList}
              showDetails={false}
            />
          }
        />
      )}
    </>
  );
};